// StarRatingInput.js
import React, { useState } from 'react';
import { Star, X, Loader2 } from 'lucide-react';

const StarRatingInput = ({ shiftId, onRate, isSubmitting }) => {
  const [hoverValue, setHoverValue] = useState(0);
  const [selectedStars, setSelectedStars] = useState(0);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [discipline, setDiscipline] = useState("");
  const [qualityOfWork, setQualityOfWork] = useState("");
  const [behaviour, setBehaviour] = useState("");
  const [isSubmittingReason, setIsSubmittingReason] = useState(false);

  const handleStarClick = (starValue) => {
    if (isSubmitting) return;
    setSelectedStars(starValue);
    
    // If rating is less than or equal to 3, show reason modal
    if (starValue <= 3) {
      setShowReasonModal(true);
    } else {
      // For ratings 4 or 5, submit directly
      onRate({ stars: starValue });
    }
  };

  const handleReasonSubmit = () => {
    // Allow submission even if fields are empty (optional)
    setIsSubmittingReason(true);
    // Submit the rating with the reason
    onRate({
      stars: selectedStars,
      discipline: discipline,
      qualityOfWork: qualityOfWork,
      behaviour: behaviour
    });
    setIsSubmittingReason(false);
    setShowReasonModal(false);
    setDiscipline("");
    setQualityOfWork("");
    setBehaviour("");
    setSelectedStars(0);
  };

  const handleCloseReasonModal = () => {
    setShowReasonModal(false);
    setDiscipline("");
    setQualityOfWork("");
    setBehaviour("");
    setSelectedStars(0);
  };

  return (
    <>
      <div className="flex items-center justify-center gap-0.5">
        {[1, 2, 3, 4, 5].map((starValue) => (
          <Star
            key={starValue}
            className={`h-4 w-4 transition-colors ${
              starValue <= hoverValue ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
            } ${isSubmitting ? "opacity-50 pointer-events-none" : "cursor-pointer hover:text-yellow-400"}`}
            onMouseEnter={() => !isSubmitting && setHoverValue(starValue)}
            onMouseLeave={() => !isSubmitting && setHoverValue(0)}
            onClick={() => !isSubmitting && handleStarClick(starValue)}
          />
        ))}
        {isSubmitting && <Loader2 className="h-3 w-3 animate-spin text-indigo-600 ml-1" />}
      </div>

      {/* Reason Modal for Low Ratings (1-3 stars) */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1400] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                Provide Feedback for {selectedStars} Star Rating
              </h3>
              <button onClick={handleCloseReasonModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-2 mb-3">
                {[1, 2, 3, 4, 5].map((starValue) => (
                  <Star
                    key={starValue}
                    className={`h-5 w-5 ${
                      starValue <= selectedStars ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-600 mb-4">
                You've given a {selectedStars}-star rating. Please provide feedback to help us improve.
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discipline
                  </label>
                  <input
                    type="text"
                    value={discipline}
                    onChange={(e) => setDiscipline(e.target.value)}
                    placeholder="Enter discipline feedback..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quality of Work
                  </label>
                  <input
                    type="text"
                    value={qualityOfWork}
                    onChange={(e) => setQualityOfWork(e.target.value)}
                    placeholder="Enter quality feedback..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Behaviour
                  </label>
                  <input
                    type="text"
                    value={behaviour}
                    onChange={(e) => setBehaviour(e.target.value)}
                    placeholder="Enter behaviour feedback..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleCloseReasonModal}
                  className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReasonSubmit}
                  disabled={isSubmittingReason}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmittingReason ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Feedback"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StarRatingInput;